<template>
  <div class="po-dash-page dash-home">
    <NavbarAdmin v-if="esAdmin()" />
    <NavbarMain v-else />

    <div class="container po-dash-container po-dash-container--wide py-4 pb-5">
      <header class="dash-hero mb-4">
        <p class="po-dash-head__eyebrow mb-1">Panel</p>
        <h1 class="po-dash-head__title h3 mb-0 d-inline-flex flex-wrap align-items-center justify-content-center justify-content-md-start gap-2">
          <span>Hola, {{ nombreNegocio }}</span>
          <router-link
            v-if="avisoPlanPorVencer && gestion"
            to="/u/planes"
            class="badge rounded-pill text-bg-warning text-decoration-none dash-vence-badge"
            :title="textoPlanPorVencer"
          >
            {{ textoPlanPorVencer }}
          </router-link>
          <span
            v-else-if="avisoPlanPorVencer"
            class="badge rounded-pill text-bg-warning dash-vence-badge"
          >
            {{ textoPlanPorVencer }}
          </span>
        </h1>
        <p v-if="!esAdmin()" class="po-dash-head__lead text-muted small mb-0">
          {{ homeLeadText }}
        </p>
        <p v-else class="po-dash-head__lead text-muted small mb-0">
          Panel central de administración de Puestify
        </p>
        <p
          v-if="!esAdmin() && guiaVenderGastro"
          class="small text-muted mt-2 mb-0"
        >
          <strong>¿Qué uso?</strong>
          Mesas para el salón · Cobrar (POS) para mostrador · Pedidos para WhatsApp / delivery.
        </p>
      </header>

      <div v-if="esAdmin()" class="admin-panel">
        <AdminDashboard />
      </div>

      <div v-else>
        <div
          v-if="!appOnline"
          class="alert alert-secondary border-0 shadow-sm rounded-3 mb-4"
        >
          <div class="small mb-0">
            <strong
              ><i class="bi bi-wifi-off me-1" aria-hidden="true"></i>Sin
              conexión.</strong
            >
            <span>
              Podés usar el POS
              <template v-if="feat('mesas')"> y Mesas</template>
              offline: cobrar con efectivo, tarjeta o transferencia y guardar
              para sincronizar después.
            </span>
          </div>
        </div>

        <div
          v-if="!appOnline && (feat('pos') || feat('mesas') || feat('caja'))"
          class="dash-sections mb-4"
          aria-label="Accesos offline"
        >
          <section class="dash-section">
            <h2 class="dash-section__title">
              <i class="bi bi-wifi-off" aria-hidden="true"></i> Modo offline
            </h2>
            <nav class="dash-tiles">
              <router-link v-if="feat('pos')" class="dash-tile" to="/u/pos">
                <i
                  class="bi bi-cash-coin dash-tile__ico"
                  aria-hidden="true"
                ></i>
                <span>Cobrar (POS)</span>
              </router-link>
              <router-link v-if="feat('mesas')" class="dash-tile" to="/u/mesas">
                <i
                  class="bi bi-fork-knife dash-tile__ico"
                  aria-hidden="true"
                ></i>
                <span>Gestión de mesas</span>
              </router-link>
              <router-link v-if="feat('caja')" class="dash-tile" to="/u/caja">
                <i class="bi bi-safe dash-tile__ico" aria-hidden="true"></i>
                <span>Caja actual</span>
              </router-link>
              <router-link v-if="feat('pos')" class="dash-tile" to="/u/cobros">
                <i class="bi bi-wallet2 dash-tile__ico" aria-hidden="true"></i>
                <span>Ventas del local</span>
              </router-link>
              <router-link class="dash-tile" to="/u/productos">
                <i
                  class="bi bi-grid-3x3-gap dash-tile__ico"
                  aria-hidden="true"
                ></i>
                <span>Productos</span>
              </router-link>
              <router-link v-if="feat('pos')" class="dash-tile" to="/u/etiquetas">
                <i class="bi bi-tags dash-tile__ico" aria-hidden="true"></i>
                <span>Etiquetas de góndola</span>
              </router-link>
              <router-link v-if="feat('stock')" class="dash-tile" to="/u/stock">
                <i
                  class="bi bi-clock-history dash-tile__ico"
                  aria-hidden="true"
                ></i>
                <span>Movimientos stock</span>
              </router-link>
            </nav>
          </section>
        </div>

        <template v-if="appOnline">
          <div
            v-if="avisoPlanVencido"
            class="alert alert-warning border-0 shadow-sm rounded-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
          >
            <div class="small mb-0">
              <strong>Plan vencido.</strong>
              No podés usar el panel ni la tienda pública hasta renovar.
              Contratá un plan para reactivar todo.
            </div>
            <router-link
              to="/u/planes"
              class="btn btn-sm btn-dark rounded-pill flex-shrink-0"
              >Ver planes</router-link
            >
          </div>

          <OnboardingChecklist v-if="nombreUsuario" :usuario="nombreUsuario" />

          <div
            v-if="resumenReferidos && !esVendedorSesion"
            class="alert alert-success border-0 shadow-sm rounded-3 mb-4"
          >
            <div class="small mb-0">
              <strong
                ><i class="bi bi-people me-1" aria-hidden="true"></i>Referí y
                ahorrá</strong
              >
              <p class="mb-1 mt-1">
                Tu código de referido es
                <code class="user-select-all">{{ nombreUsuario }}</code
                >. Cada negocio que se registre con tu usuario te da
                <strong>{{
                  fmtReferido(resumenReferidos.descuentoPorReferido || 2000)
                }}</strong>
                de descuento cuando complete los primeros pasos del panel (máx.
                {{ resumenReferidos.maxReferidos }}).
              </p>
              <p class="mb-0 text-muted">
                Referidos: {{ resumenReferidos.totalReferidos }}/{{
                  resumenReferidos.maxReferidos
                }}
                <span v-if="resumenReferidos.creditosEnEsperaChecklist > 0">
                  · {{ resumenReferidos.creditosEnEsperaChecklist }} esperando
                  checklist
                </span>
                <span v-if="resumenReferidos.descuentoPendiente > 0">
                  · Pendiente:
                  <strong>{{
                    fmtReferido(resumenReferidos.descuentoPendiente)
                  }}</strong>
                </span>
                <router-link to="/u/planes" class="ms-1"
                  >Ver planes</router-link
                >
              </p>
            </div>
          </div>

          <div
            v-if="alertasProductos.total"
            class="alert alert-warning border-0 shadow-sm rounded-3 mb-4"
          >
            <div
              class="d-flex flex-wrap align-items-center justify-content-between gap-2"
            >
              <div class="small mb-0">
                <strong
                  ><i
                    class="bi bi-exclamation-triangle me-1"
                    aria-hidden="true"
                  ></i
                  >Productos críticos:</strong
                >
                {{ alertasProductos.agotados }} agotado(s) ·
                {{ alertasProductos.stockBajo }} stock bajo ·
                {{ alertasProductos.porVencer }} por vencer ·
                {{ alertasProductos.vencidos }} vencido(s)
              </div>
              <router-link
                to="/u/productos"
                class="btn btn-sm btn-dark rounded-pill"
                >Ver productos</router-link
              >
            </div>
          </div>

          <div class="dash-profile card border-0 shadow-sm mb-4 mx-auto">
            <div class="card-body dash-profile__body py-4 px-3 px-md-4">
              <div class="dash-avatar-wrap mb-3 mb-md-0">
                <img
                  v-if="negocio && negocio.imagen"
                  :src="negocio.imagen"
                  width="96"
                  height="96"
                  alt=""
                  class="rounded-circle object-fit-cover shadow"
                />
                <div
                  v-else
                  class="dash-avatar-placeholder rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                >
                  <i class="bi bi-shop fs-1 text-white" aria-hidden="true"></i>
                </div>
              </div>
              <div class="dash-profile__text text-center text-md-start">
                <p class="text-muted small mb-0">
                  Tocá un acceso para ir a cada sección. En el celular usá
                  también la barra inferior.
                </p>
              </div>
            </div>
          </div>

          <div class="dash-sections" aria-label="Accesos del panel">
            <section class="dash-section">
              <h2 class="dash-section__title">
                <i class="bi bi-cash-stack" aria-hidden="true"></i> Vender
              </h2>
              <nav class="dash-tiles">
                <router-link
                  v-if="feat('pos')"
                  class="dash-tile"
                  to="/u/pos"
                >
                  <i
                    class="bi bi-cash-coin dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Cobrar (POS)</span>
                </router-link>
                <router-link
                  v-if="feat('mesas')"
                  class="dash-tile"
                  to="/u/mesas"
                >
                  <i
                    class="bi bi-fork-knife dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Gestión de mesas</span>
                </router-link>
                <router-link
                  v-if="feat('caja')"
                  class="dash-tile"
                  to="/u/caja"
                >
                  <i class="bi bi-safe dash-tile__ico" aria-hidden="true"></i>
                  <span>Caja (abrir turno)</span>
                </router-link>
                <router-link
                  v-if="feat('pedido_mostrador')"
                  class="dash-tile"
                  to="/u/pedido-mostrador"
                >
                  <i
                    class="bi bi-cart-plus dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Tomar pedido</span>
                </router-link>
                <router-link
                  v-if="featAny(['carrito_online', 'pedido_mostrador', 'mesas'])"
                  class="dash-tile"
                  to="/u/pedidos"
                >
                  <i
                    class="bi bi-receipt-cutoff dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Pedidos</span>
                </router-link>
                <router-link
                  v-if="feat('pos')"
                  class="dash-tile"
                  to="/u/cobros"
                >
                  <i
                    class="bi bi-wallet2 dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Ventas POS</span>
                </router-link>
                <router-link
                  v-if="gestion && feat('analiticas')"
                  class="dash-tile dash-tile--analytics"
                  to="/u/analiticas"
                >
                  <i
                    class="bi bi-graph-up-arrow dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Analíticas</span>
                </router-link>
              </nav>
            </section>

            <section class="dash-section">
              <h2 class="dash-section__title">
                <i class="bi bi-grid-3x3-gap" aria-hidden="true"></i> Catálogo
              </h2>
              <nav class="dash-tiles">
                <router-link class="dash-tile" to="/u/productos">
                  <i
                    class="bi bi-grid-3x3-gap dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Productos</span>
                </router-link>
                <router-link
                  v-if="gestion && feat('pos')"
                  class="dash-tile"
                  to="/u/etiquetas"
                >
                  <i class="bi bi-tags dash-tile__ico" aria-hidden="true"></i>
                  <span>Etiquetas de góndola</span>
                </router-link>
                <router-link
                  v-if="feat('stock')"
                  class="dash-tile"
                  to="/u/stock"
                >
                  <i
                    class="bi bi-clock-history dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Movimientos stock</span>
                </router-link>
                <router-link
                  v-if="puedeGestionarPublicaciones && feat('publicaciones')"
                  class="dash-tile"
                  to="/u/publicaciones"
                >
                  <i class="bi bi-images dash-tile__ico" aria-hidden="true"></i>
                  <span>Publicaciones</span>
                </router-link>
                <router-link
                  v-if="gestion && feat('menu_online')"
                  class="dash-tile"
                  :to="'/' + nombreUsuario"
                >
                  <i
                    class="bi bi-shop-window dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Mi tienda</span>
                </router-link>
                <router-link
                  v-if="gestion && feat('menu_online')"
                  class="dash-tile"
                  to="/u/crearqr"
                >
                  <i
                    class="bi bi-qr-code dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>QR del menú</span>
                </router-link>
              </nav>
            </section>

            <section v-if="gestion" class="dash-section">
              <h2 class="dash-section__title">
                <i class="bi bi-gear" aria-hidden="true"></i> Gestión
              </h2>
              <nav class="dash-tiles">
                <router-link
                  v-if="feat('clientes')"
                  class="dash-tile"
                  to="/u/clientes"
                >
                  <i class="bi bi-people dash-tile__ico" aria-hidden="true"></i>
                  <span>Clientes</span>
                </router-link>
                <router-link
                  v-if="feat('caja')"
                  class="dash-tile"
                  to="/u/cajas"
                >
                  <i class="bi bi-safe2 dash-tile__ico" aria-hidden="true"></i>
                  <span>Cierres de caja</span>
                </router-link>
                <router-link
                  v-if="feat('vendedores')"
                  class="dash-tile"
                  to="/u/modificar?seccion=equipo"
                >
                  <i
                    class="bi bi-person-badge dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Equipo</span>
                </router-link>
                <router-link
                  v-if="feat('tickets')"
                  class="dash-tile"
                  to="/u/ticket"
                >
                  <i
                    class="bi bi-receipt dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Tickets</span>
                </router-link>
                <router-link class="dash-tile" to="/u/logs">
                  <i
                    class="bi bi-clock-history dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Actividad</span>
                </router-link>
                <router-link class="dash-tile" to="/u/modificar">
                  <i
                    class="bi bi-person-gear dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Perfil</span>
                </router-link>
                <button
                  type="button"
                  class="dash-tile dash-tile--ghost border-0 bg-transparent"
                  @click="enviarMensaje"
                >
                  <i
                    class="bi bi-chat-dots dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Soporte</span>
                </button>
              </nav>
            </section>

            <section v-else class="dash-section">
              <h2 class="dash-section__title">
                <i class="bi bi-life-preserver" aria-hidden="true"></i> Ayuda
              </h2>
              <nav class="dash-tiles">
                <button
                  type="button"
                  class="dash-tile dash-tile--ghost border-0 bg-transparent"
                  @click="enviarMensaje"
                >
                  <i
                    class="bi bi-chat-dots dash-tile__ico"
                    aria-hidden="true"
                  ></i>
                  <span>Soporte</span>
                </button>
              </nav>
            </section>
          </div>
        </template>

        <div v-if="appOnline" class="text-center mt-4 pt-1">
          <button
            type="button"
            class="btn btn-outline-danger rounded-pill px-4"
            data-bs-toggle="modal"
            data-bs-target="#cerrarSesionDashModal"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>

    <ConfirmLogoutModal
      modal-id="cerrarSesionDashModal"
      @confirm="cerrarSesion"
    />
  </div>
</template>

<script>
import NavbarAdmin from "@/components/layout/NavbarAdmin.vue";
import AdminDashboard from "@/components/admin/AdminDashboard.vue";
import NavbarMain from "@/components/layout/NavbarMain.vue";
import ConfirmLogoutModal from "@/components/shared/ConfirmLogoutModal.vue";
import OnboardingChecklist from "@/components/shared/OnboardingChecklist.vue";
import router from "@/router";
import { RouterLink } from "vue-router";
import axios from "axios";
import Swal from "sweetalert2";
import {
  isAdmin,
  puedeGestionarNegocio,
  getVendedorNombre,
  cerrarSesionPanel,
} from "@/utils/session";
import { hasFeature, hasAnyFeature } from "@/utils/features";
import {
  connectivity,
  isOnline,
  onConnectivityChange,
} from "@/utils/connectivity";
import { ensureOfflineCacheWarm } from "@/utils/posOffline/cache";
import { prefetchOfflineShellChunks } from "@/utils/prefetchOfflineShell";
import { getHomeLeadText } from "@/utils/panelNav";

export default {
  components: {
    NavbarAdmin,
    AdminDashboard,
    NavbarMain,
    ConfirmLogoutModal,
    OnboardingChecklist,
    "router-link": RouterLink,
  },
  data() {
    return {
      nombreUsuario: "",
      nombreNegocio: "",
      negocio: "",
      alertasProductos: {
        total: 0,
        agotados: 0,
        stockBajo: 0,
        porVencer: 0,
        vencidos: 0,
      },
      resumenReferidos: null,
      offConnectivity: null,
      homeLeadText: getHomeLeadText(),
    };
  },
  computed: {
    appOnline() {
      return connectivity.online;
    },
    gestion() {
      return puedeGestionarNegocio();
    },
    esVendedorSesion() {
      return !!getVendedorNombre();
    },
    puedeGestionarPublicaciones() {
      return this.gestion && !this.esVendedorSesion;
    },
    avisoPlanVencido() {
      if (this.esAdmin() || !this.negocio) return false;
      if (this.negocio.planSuspendido === true) return true;
      if (!this.negocio.fechaVence) return false;
      try {
        return new Date() >= new Date(this.negocio.fechaVence);
      } catch {
        return false;
      }
    },
    avisoPlanPorVencer() {
      if (this.esAdmin() || !this.negocio || this.avisoPlanVencido) return false;
      if (!this.negocio.fechaVence) return false;
      const vencimiento = new Date(this.negocio.fechaVence);
      if (Number.isNaN(vencimiento.getTime())) return false;
      const ahora = new Date();
      if (vencimiento < ahora) return false;
      const limite = new Date();
      limite.setDate(limite.getDate() + 7);
      limite.setHours(23, 59, 59, 999);
      return vencimiento <= limite;
    },
    diasRestantesPlan() {
      if (!this.negocio || !this.negocio.fechaVence) return null;
      const vencimiento = new Date(this.negocio.fechaVence);
      if (Number.isNaN(vencimiento.getTime())) return null;
      const ahora = new Date();
      const ms = vencimiento.getTime() - ahora.getTime();
      return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    },
    textoPlanPorVencer() {
      const d = this.diasRestantesPlan;
      if (d == null) return 'Vence pronto';
      if (d <= 0) return 'Vence hoy';
      if (d === 1) return 'Vence mañana';
      return `Vence en ${d} días`;
    },
    guiaVenderGastro() {
      return (
        this.feat('mesas') &&
        this.feat('pos') &&
        (this.feat('carrito_online') || this.feat('menu_online'))
      );
    },
  },
  mounted() {
    this.homeLeadText = getHomeLeadText();
    this.leerUsuario();
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (this.appOnline && this.nombreUsuario) {
      void Promise.all([
        ensureOfflineCacheWarm(this.nombreUsuario),
        prefetchOfflineShellChunks(),
      ]);
    }
    let wasOnline = this.appOnline;
    this.offConnectivity = onConnectivityChange((online) => {
      if (online && !wasOnline && this.nombreUsuario) {
        void Promise.all([
          ensureOfflineCacheWarm(this.nombreUsuario),
          prefetchOfflineShellChunks(),
        ]);
      }
      wasOnline = online;
    });
  },
  beforeUnmount() {
    if (this.offConnectivity) this.offConnectivity();
  },
  created() {
    if (!this.esAdmin()) {
      if (isOnline()) {
        this.obtenerInformacionNegocio();
        this.cargarAlertasProductos();
        this.cargarResumenReferidos();
      }
    }
  },
  methods: {
    feat(key) {
      return hasFeature(key);
    },
    featAny(keys) {
      return hasAnyFeature(...keys);
    },
    fmtReferido(val) {
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 0,
      }).format(Number(val) || 0);
    },
    async cargarResumenReferidos() {
      const usuario = localStorage.getItem("usuario");
      if (!usuario || usuario === "admin") return;
      try {
        const { data } = await axios.get("/referidos/resumen", {
          params: { usuario },
        });
        this.resumenReferidos = data;
      } catch (e) {
        console.error("referidos resumen:", e);
        this.resumenReferidos = {
          totalReferidos: 0,
          maxReferidos: 5,
          creditosEnEsperaChecklist: 0,
          descuentoPendiente: 0,
          descuentoPorReferido: 2000,
        };
      }
    },
    enviarMensaje() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          userAgent,
        );
      const baseLink = isMobile
        ? "whatsapp://send"
        : "https://web.whatsapp.com/send";
      const mensajeCompleto = "¡Hola Puestify! Tengo una consulta...";
      const numeroWhatsapp = `${baseLink}?phone=3853005369&text=${encodeURIComponent(
        mensajeCompleto,
      )}`;
      try {
        window.open(numeroWhatsapp, "_blank", "noopener,noreferrer");
      } catch (error) {
        const extra =
          error && error.response && error.response.data
            ? error.response.data.message
            : "";
        Swal.fire({
          icon: "error",
          text: "No se pudo abrir WhatsApp. " + (extra || ""),
        });
      }
    },
    async obtenerInformacionNegocio() {
      try {
        const response = await axios.get(
          `/miNegocio?usuario=${localStorage.getItem("usuario")}`,
        );
        this.negocio = response.data;
      } catch (error) {
        console.error("Error al obtener información del negocio:", error);
      }
    },
    async cargarAlertasProductos() {
      const u = localStorage.getItem("usuario");
      if (!u) return;
      try {
        const { data } = await axios.get("/productos/alertas", {
          params: { usuario: u },
        });
        this.alertasProductos = {
          total: data.total || 0,
          agotados: data.agotados || 0,
          stockBajo: data.stockBajo || 0,
          porVencer: data.porVencer || 0,
          vencidos: data.vencidos || 0,
        };
      } catch (e) {
        console.warn("Alertas productos:", e);
      }
    },
    leerUsuario() {
      this.nombreUsuario = localStorage.getItem("usuario") || "";
      const vend = getVendedorNombre();
      this.nombreNegocio = vend || localStorage.getItem("nombre") || "negocio";
    },
    esAdmin() {
      return isAdmin();
    },
    cerrarSesion() {
      if (!cerrarSesionPanel(router)) {
        void Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: "Sin conexión",
          text: "Cerrá sesión cuando vuelva internet.",
          showConfirmButton: false,
          timer: 3500,
        });
      }
    },
  },
};
</script>

<style scoped>
.dash-vence-badge {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 0.4em 0.75em;
  vertical-align: middle;
}

@media (min-width: 768px) {
  .dash-hero {
    text-align: left;
  }
}

.dash-profile {
  max-width: 720px;
}

.dash-profile__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

@media (min-width: 768px) {
  .dash-profile__body {
    flex-direction: row;
    align-items: center;
    text-align: left;
    gap: 1.25rem;
  }

  .dash-profile__body .dash-avatar-wrap {
    margin-bottom: 0 !important;
  }

  .dash-profile__text {
    flex: 1;
    min-width: 0;
  }
}

.dash-avatar-wrap img {
  object-fit: cover;
}

.dash-avatar-placeholder {
  width: 96px;
  height: 96px;
  background: linear-gradient(135deg, #2563eb, #0d9488);
}

.admin-panel {
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.admin-stat {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 0.9rem 1rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  height: 100%;
}

.admin-stat__ico {
  font-size: 1.6rem;
  color: #6366f1;
  flex-shrink: 0;
}

.admin-stat--ok .admin-stat__ico {
  color: #16a34a;
}

.admin-stat--warn .admin-stat__ico {
  color: #dc2626;
}

.admin-stat--soon .admin-stat__ico {
  color: #d97706;
}

.admin-stat__num {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.admin-stat__label {
  display: block;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
}

.dash-sections {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.dash-section__title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 0.65rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.dash-section__title i {
  color: #2563eb;
  font-size: 0.95rem;
}

/* Grilla responsive: de 2 columnas en el móvil hasta 4–5 en escritorio */
.dash-tiles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

@media (min-width: 480px) {
  .dash-tiles {
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
    gap: 1rem;
  }
}

@media (min-width: 1200px) {
  .dash-tiles {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
}

.dash-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 108px;
  padding: 1rem 0.65rem;
  text-decoration: none;
  color: #0f172a;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: center;
  width: 100%;
}

.dash-tile:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1);
  border-color: #cbd5e1;
  color: #0f172a;
}


.dash-tile--analytics .dash-tile__ico {
    color: #7c3aed;
}

.dash-tile--ghost:hover {
  background: #f8fafc !important;
}

.dash-tile__ico {
  font-size: 1.65rem;
  color: #2563eb;
}

.dash-tile--ghost .dash-tile__ico {
  color: #64748b;
}

@media (min-width: 768px) {
  .dash-tile {
    min-height: 118px;
    font-size: 0.875rem;
  }

  .dash-tile__ico {
    font-size: 1.85rem;
  }
}
</style>
